import { Link } from 'react-router-dom';
import {
  Briefcase,
  FileText,
  DollarSign,
  Clock,
  Plus,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { useProjects, useBids, useInvoices, useTimeEntriesSummary } from '@/hooks/use-projects';
import { useShowInvoicing } from '@/hooks/use-show-invoicing';
import { useIndustry } from '@/hooks/use-industry';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney, OPTIONAL_FRACTION_DIGITS } from '@/lib/currency';
import { formatDateOnly, getLocalToday, getLocalWeekStart } from '@/lib/date-utils';
import { usePermissions } from '@/hooks/use-permissions';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  bidding: 'bg-blue-100 text-blue-800',
  awarded: 'bg-purple-100 text-purple-800',
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

export function ProjectsDashboardPage() {
  const currency = useOrgCurrency();
  const showInvoicing = useShowInvoicing();
  const { labels } = useIndustry();
  const { data: activeProjects } = useProjects({ status: 'active', page_size: 10 });
  const { data: _allProjects } = useProjects({ page_size: 1 });
  const { data: pendingBids } = useBids({ status: 'sent', page_size: 1 });
  const { data: overdueInvoices } = useInvoices({ status: 'overdue', page_size: 10 });
  const { data: unpaidInvoices } = useInvoices({ status: 'sent', page_size: 1 });

  const today = getLocalToday();
  const weekStart = getLocalWeekStart();
  const { data: weekHoursData } = useTimeEntriesSummary({
    date_from: weekStart,
    date_to: today,
  });

  const weekHours = weekHoursData?.total_hours ?? '-';

  const summaryCards = [
    {
      title: 'Active Projects',
      value: activeProjects?.count ?? '-',
      icon: Briefcase,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      link: '/projects/list?status=active',
    },
    ...(showInvoicing
      ? [
          {
            title: 'Pending Bids',
            value: pendingBids?.count ?? '-',
            icon: FileText,
            color: 'text-purple-600',
            bg: 'bg-purple-50',
            link: '/projects/list',
          },
          {
            title: 'Unpaid Invoices',
            value: unpaidInvoices?.count ?? '-',
            icon: DollarSign,
            color: 'text-orange-600',
            bg: 'bg-orange-50',
            link: '/projects/list',
          },
        ]
      : []),
    {
      title: 'Hours This Week',
      value: weekHours,
      icon: Clock,
      color: 'text-green-600',
      bg: 'bg-green-50',
      link: '/projects/time',
    },
  ];

  const { canViewClients } = usePermissions();
  const quickActions = [
    { label: 'New Project', icon: Plus, to: '/projects/new' },
    { label: 'View Projects', icon: Briefcase, to: '/projects/list' },
    ...(canViewClients ? [{ label: `${labels.client}s`, icon: FileText, to: '/clients' }] : []),
    { label: 'Log Time', icon: Clock, to: '/projects/time' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1><img src="/logos/h3books.svg" alt="H3Books" className="h-7" /></h1>
          <p className="text-gray-600 mt-1">Billing &amp; project finances</p>
        </div>
        <Button asChild>
          <Link to="/projects/new">
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Link>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <Link key={card.title} to={card.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{card.title}</p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${card.bg}`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Overdue Invoices Alert */}
      {showInvoicing && overdueInvoices && overdueInvoices.results.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Overdue Invoices ({overdueInvoices.count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overdueInvoices.results.slice(0, 5).map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {invoice.invoice_number} — {invoice.client_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      Due: {formatDateOnly(invoice.due_date)} &middot; {invoice.project_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-700">
                      {formatMoney(invoice.balance_due, currency, OPTIONAL_FRACTION_DIGITS)}
                    </p>
                    <Badge className="bg-red-100 text-red-800">Overdue</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Projects List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Active Projects
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/projects/list?status=active">
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {activeProjects && activeProjects.results.length > 0 ? (
            <div className="space-y-3">
              {activeProjects.results.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{project.name}</p>
                    <p className="text-sm text-gray-500">
                      {project.project_number}
                      {project.client_name ? ` — ${project.client_name}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusColors[project.status] ?? 'bg-gray-100 text-gray-800'}>
                      {project.status.replace('_', ' ')}
                    </Badge>
                    {project.budget && (
                      <span className="text-sm text-gray-500">
                        {formatMoney(project.budget, currency, OPTIONAL_FRACTION_DIGITS)}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No active projects</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
                asChild
              >
                <Link to={action.to}>
                  <action.icon className="h-5 w-5" />
                  <span className="text-sm">{action.label}</span>
                </Link>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
